import React, { useContext, useEffect, useState } from "react";
import "../ScopeManagement.scss";
import ClassNames from "classnames";
import { ScopesContext, type ScopeType } from "../ScopeProvider/ScopeProvider";
import ScopeAndGroupsListManagementPopover from "../../../Findings/FindingsFilters/ScopeHierarchy/ScopeManagement/ScopeAndGroupsListManagementPopover/ScopeAndGroupsListManagementPopover";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../General/AppPreferencesProvider";
import { useRouteMatch } from "react-router-dom";
import LocationBreadcrumbs from "../LocationBreadCrumbs/LocationBreadcrumbs";
import CreateGroup from "../../../Findings/FindingsFilters/ScopeHierarchy/CreateGroup/CreateGroup";
import { UNSAVED_SCOPE } from "../../../Findings/FindingsFilters/FilterUtils";
import {
  getIconClassName,
  resourceTypes,
} from "../../../Findings/FindingsUtils";
import { pathLevels } from "../../AppViewUtils";
import { SeemPopover, ModalContext } from "@SeemplicityDev/shared";
import { getIconByType } from "../../../Components/Tree/TreeUtils";

export interface ScopeManagementBarProps {
  wrapperClassName?: string;
  isLoadingScopesData?: boolean;
  editable?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  showAsLabel?: boolean;
  displayAsInput?: boolean;
  selectableNodeType?: string;
  showHeader?: boolean;
  locationContext?: string;
  useSameWidth?: boolean;
}

// Legacy Chrome `Event.path` (non-standard composedPath) used by the outer-action
// handler; not present in lib.dom typings, so we widen the event type here.
interface OuterActionEvent {
  path?: { className?: string }[];
}

const ScopeManagementBar: React.FC<ScopeManagementBarProps> = ({
  wrapperClassName,
  isLoadingScopesData,
  editable,
  readOnly,
  disabled,
  showAsLabel,
  displayAsInput,
  selectableNodeType,
  showHeader,
  locationContext,
  useSameWidth,
}) => {
  const {
    scopesState,
    scopesState: { scopesList, selectedScope },
    updateScopeState,
    searchTree,
  } = useContext(ScopesContext);
  const match = useRouteMatch<{ item: string }>("/:item");
  const { isAllowed } = useContext(AppPreferencesContext);
  const [isOpen, setIsOpen] = useState(false);
  const { openModal } = useContext(ModalContext);
  const [activeListNode, setActiveListNode] = useState<string | null>(null);

  useEffect(() => {
    if (scopesState.shouldOpenSelector) {
      setIsOpen(true);
      updateScopeState({ shouldOpenSelector: false });
    }
  }, [scopesState.shouldOpenSelector, updateScopeState]);

  const createNewScope = () => {
    const level =
      selectedScope?.value?.type === resourceTypes.SCOPE
        ? selectedScope?.level
        : (selectedScope?.level as number) + 1;
    updateScopeState({
      isNewScope: true,
      selectedScope: {
        level,
        label: UNSAVED_SCOPE,
        value: {
          // resourceTypes.SCOPE is the string literal "scope", a valid ScopeType.
          type: resourceTypes.SCOPE as ScopeType,
        },
        parentId:
          selectedScope?.value?.type === resourceTypes.GROUP
            ? selectedScope?.value?.id
            : selectedScope?.parentId,
      },
      scopeToExecute: null,
    });
    setIsOpen(false);
  };

  const createNewGroup = () => {
    setIsOpen(false);
    openModal({
      title: "Create a Group",
      className: "edit-group-modal",
      size: "s",
      overlay: true,
      component: (
        <CreateGroup
          groupId={undefined}
          groupName={undefined}
          selectedScope={selectedScope}
          searchTree={searchTree}
          root={!!scopesList ? scopesList : null}
          updateScopeState={updateScopeState}
        />
      ),
    });
  };

  const classNameWrapper = ClassNames(wrapperClassName && wrapperClassName, {
    "": !displayAsInput,
    "input-holder-inner-wrap": displayAsInput,
  });

  const onOuterActionHandler = (e: OuterActionEvent) => {
    const isFilterDropdown = e?.path?.filter((path) => {
      return (
        path?.className?.includes("group-location-select") ||
        path?.className?.includes("scope-management-actions-menu") ||
        path?.className?.includes("tags-list") ||
        path?.className?.includes("save-scope-warning") ||
        path?.className?.includes("filter-save-as") ||
        path?.className?.includes("toast-bottom-right")
      );
    });
    if (!isFilterDropdown?.length) {
      setTimeout(() => setIsOpen(false), 200);
    }
  };

  const toggleIsOpen = () => setIsOpen(!isOpen);

  const locationBreadcrumbsClick = (activeNodeForListType: string) => {
    setIsOpen(true);

    if (
      activeNodeForListType === pathLevels.CURRENT ||
      activeNodeForListType === pathLevels.ROOT
    ) {
      setActiveListNode(selectedScope?.value?.id ?? null);
    } else {
      // if it isn't the current one, it'll be its direct parent
      setActiveListNode(selectedScope?.parentId ?? null);
    }
  };

  return (
    <div
      data-testid={`scope-management`}
      data-cy={
        displayAsInput
          ? "scope-management-with-input-label"
          : "scope-management-with-breadcrumbs"
      }
      className={`${classNameWrapper}
          ${isLoadingScopesData ? "is-loading" : ""}`}
    >
      <SeemPopover
        isOpen={isOpen}
        placement={"bottom-start"}
        triggerFullWidth
        useSameWidth={useSameWidth}
        body={
          <ScopeAndGroupsListManagementPopover
            showHeader={showHeader}
            selectableNodeType={selectableNodeType}
            activeListNode={activeListNode}
            createNewScope={createNewScope}
            createNewGroup={createNewGroup}
            toggleIsOpen={toggleIsOpen}
            editable={isAllowed(userActionsEnum.EDIT_SCOPE) && editable}
            displayAsInput={displayAsInput}
          />
        }
        className={`scope-popover-class-${match?.params?.item} ${locationContext}`}
        // SeemPopover's onOuterAction is typed `() => void` and is invoked with
        // no arguments at runtime (setTimeout(onOuterAction, 200)), so the `e`
        // param is always undefined here. The handler already guards with `?.`,
        // so behavior is preserved; cast bridges the signature mismatch.
        onOuterAction={onOuterActionHandler as () => void}
      >
        {showAsLabel ? (
          <div
            data-testid={"management-input-wrapper"}
            className={`input-holder-inner ${isOpen ? "is-open" : ""}`}
            onClick={() => {
              if (!readOnly && !disabled) {
                toggleIsOpen();
              }
            }}
          >
            {selectedScope?.value ? (
              <div className="input-holder-icon">
                <i
                  className={`seem-icon ${getIconClassName(selectedScope?.value?.type)}`}
                />
              </div>
            ) : (
              <div className="input-holder-icon">
                <i
                  className={`seem-icon ${getIconByType(resourceTypes.SCOPE)}`}
                />
              </div>
            )}
            <div
              className={`input-holder-text ${
                selectedScope?.label ? "has-label" : "placeholder-text"
              }`}
            >
              {selectedScope?.label || "Select Scope"}
            </div>
            {!readOnly && !disabled && (
              <div className="dropdown-indicator">
                <i className="seem-icon seem-icon-chevron-down" />
              </div>
            )}
          </div>
        ) : (
          <LocationBreadcrumbs
            isLoadingScopesData={isLoadingScopesData}
            closeScopesList={() => setIsOpen(false)}
            isScopesListOpen={isOpen}
            onPathItemClick={locationBreadcrumbsClick}
          />
        )}
      </SeemPopover>
    </div>
  );
};

export default ScopeManagementBar;
